import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  forwardRef,
  inject,
  Injector,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { Company, isOpenToNewContracts } from '../company.model';
import { CompanyService } from '../company.service';

@Component({
  selector: 'app-select-company',
  imports: [CommonModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './select-company.html',
  styleUrls: ['./select-company.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectCompany),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectCompany),
      multi: true,
    },
  ],
})
export class SelectCompany implements OnInit, DoCheck, ControlValueAccessor, Validator {
  readonly companyService = inject(CompanyService);
  private injector = inject(Injector);
  cdr = inject(ChangeDetectorRef);

  @ViewChild(MatSelect) private readonly matSelect?: MatSelect;

  @Input() label = 'Société';
  @Input() value: string | undefined | null;
  @Input() required: boolean | string | null | undefined;
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() nullable = false;
  /**
   * Pour un nouveau contrat : n'offrir que les sociétés en activité, en gardant affichée celle
   * déjà choisie, même cédée ou dissoute.
   */
  @Input() openOnly = false;

  ngControl?: NgControl;
  disabled = false;

  /**
   * Reflète l'état d'erreur du FormControl parent sur le mat-select interne,
   * afin que le mat-form-field passe en rouge lorsque la valeur est invalide.
   */
  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => {
      const ctrl = this.control;
      return !!(ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty));
    },
  };

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de définir le valueAccessor. Assurez-vous que ce composant est utilisé dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }
  }

  /**
   * Le mat-select interne n'est pas lié à un NgControl, donc Angular Material ne
   * recalcule jamais son état d'erreur tout seul. On le déclenche ici pour que le
   * mat-form-field reflète l'état invalide du FormControl parent (bordure rouge).
   */
  ngDoCheck(): void {
    this.matSelect?.updateErrorState();
  }

  onChange: (value: string | undefined | null) => void = () => {
    // No-op
  };
  onTouched: () => void = () => {
    // No-op
  };

  writeValue(value: string | undefined | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | undefined | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const ctrl = control ?? this.control;
    const isRequired = this.isRequired || (ctrl ? this.hasRequiredValidator(ctrl) : false);

    if (isRequired && (this.value === undefined || this.value === null || this.value === '')) {
      return { required: true };
    }

    return null;
  }

  private hasRequiredValidator(control: AbstractControl): boolean {
    if (!control.validator) return false;

    const validator = control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  /** Retourne le control du FormGroup / FormControl auquel ce composant est lié, si présent */
  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  onSelectionChange(value: string | undefined | null): void {
    this.value = value;
    this.onChange(this.value);
    this.onTouched();
  }

  isOffered(company: Company): boolean {
    return !this.openOnly || isOpenToNewContracts(company) || company.id === this.value;
  }

  get isRequired(): boolean {
    if (this.required !== undefined && this.required !== null && this.required !== '') {
      if (typeof this.required === 'boolean') {
        return this.required;
      }
      return this.required === 'true';
    }

    return this.control?.hasValidator(Validators.required) ?? false;
  }
}
